// "Carica altri servizi" + ricerca live per la griglia dell'archivio Servizi
// (archive-servizi.php), sostenute dall'endpoint REST castro/v1/servizi
// (inc/servizi-rest-controller.php): il markup PHP renderizza server-side solo
// la prima pagina (5 card, con paginazione standard di WordPress come
// fallback senza JS — vedi il filtro pre_get_posts in inc/cpt.php); qui invece
// la paginazione classica viene nascosta e sostituita da un "Carica altri
// servizi" che chiede altre card via rete (non tutte precaricate: l'archivio
// può crescere senza dover tenere ogni servizio nel DOM/nella query iniziale),
// più la ricerca live, che rifà la stessa richiesta con un termine e riparte
// da zero. "Mostra meno servizi" invece non richiede nulla al server: rimuove
// semplicemente le card aggiunte oltre la vista iniziale, già nel DOM.
import { animateCardsIn, animateCardsOut } from "./archivio-card-animations";

const SEARCH_DEBOUNCE_MS = 300;

function debounce(fn, delay) {
    let timer = null;

    return function debounced(...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
    };
}

function initArchivioServiziSection(section) {
    const grid = section.querySelector("[data-servizi-grid]");

    if (!grid) {
        return;
    }

    const searchInput = section.querySelector("[data-servizi-search-input]");
    const emptyMessage = section.querySelector("[data-servizi-search-empty]");
    const countLabel = section.querySelector("[data-servizi-count]");
    const loadMoreWrap = section.querySelector("[data-servizi-load-more-wrap]");
    const loadMoreButton = section.querySelector("[data-servizi-load-more]");
    const showLessButton = section.querySelector("[data-servizi-show-less]");
    const shownCountLabel = section.querySelector("[data-servizi-shown-count]");
    const pagination = section.querySelector(".archivio-servizi__pagination");

    const settings = (window.Castro && window.Castro.settings) || {};
    const apiUrl = `${settings.api_url || ""}castro/v1/servizi`;

    // Quanto è già renderizzato server-side (vedi $servizi_shown in
    // archive-servizi.php): è anche la dimensione a cui torna "Mostra meno".
    const baseCount = parseInt(loadMoreWrap ? loadMoreWrap.dataset.serviziShown : "0", 10) || 0;

    let shown = baseCount;
    let total = parseInt(section.dataset.serviziTotal, 10) || 0;
    let currentSearch = "";
    // Scarta le risposte di richieste superate (l'utente ha digitato altro
    // nel frattempo): solo l'ultima richiesta partita può aggiornare il DOM.
    let requestId = 0;

    // Con JS attivo la paginazione classica (unico modo di sfogliare l'elenco
    // senza JS) lascia il posto a "Carica altri servizi"/ricerca live.
    if (pagination) {
        pagination.hidden = true;
    }

    function updateCount(value) {
        if (!countLabel) {
            return;
        }

        const label = value === 1 ? "servizio" : "servizi";
        countLabel.textContent = `${value} ${label}`;
    }

    function updateLoadMoreUi(hasMore) {
        if (loadMoreWrap) {
            loadMoreWrap.hidden = total <= baseCount;
        }

        if (loadMoreButton) {
            loadMoreButton.hidden = !hasMore;
        }

        if (showLessButton) {
            showLessButton.hidden = shown <= baseCount;
        }

        if (shownCountLabel) {
            shownCountLabel.textContent = total ? `Mostrati ${shown} di ${total} servizi` : "";
        }
    }

    async function fetchServizi({ search, offset, perPage }) {
        const params = new URLSearchParams({
            search,
            offset: String(offset),
            per_page: String(perPage),
        });

        const response = await fetch(`${apiUrl}?${params.toString()}`, {
            headers: { Accept: "application/json" },
        });

        if (!response.ok) {
            throw new Error(`Richiesta fallita (${response.status})`);
        }

        return response.json();
    }

    function runSearch(search) {
        const thisRequestId = ++requestId;
        const perPage = baseCount;

        fetchServizi({ search, offset: 0, perPage })
            .then((data) => {
                if (thisRequestId !== requestId) {
                    return;
                }

                currentSearch = search;
                grid.innerHTML = data.html;
                shown = data.shown;
                total = data.total;

                if (emptyMessage) {
                    emptyMessage.hidden = total > 0;
                }

                updateCount(total);
                updateLoadMoreUi(data.hasMore);
                animateCardsIn(Array.from(grid.children));
            })
            .catch((error) => {
                // Ricerca non critica: se la richiesta fallisce si lascia la
                // griglia com'era, senza rompere il resto della pagina.
                console.error("[archivio-servizi]", error);
            });
    }

    const debouncedSearch = debounce(runSearch, SEARCH_DEBOUNCE_MS);

    if (searchInput) {
        searchInput.addEventListener("input", () => {
            debouncedSearch(searchInput.value.trim());
        });
    }

    if (loadMoreButton) {
        loadMoreButton.addEventListener("click", () => {
            const thisRequestId = ++requestId;

            loadMoreButton.classList.add("is-loading");

            fetchServizi({ search: currentSearch, offset: shown, perPage: baseCount })
                .then((data) => {
                    if (thisRequestId !== requestId) {
                        return;
                    }

                    const previousCardCount = grid.children.length;

                    // data.shown è già "offset + card restituite in questa
                    // risposta" (calcolato server-side): sostituisce shown
                    // per intero, non si somma a quello precedente.
                    grid.insertAdjacentHTML("beforeend", data.html);
                    shown = data.shown;
                    total = data.total;
                    updateLoadMoreUi(data.hasMore);
                    animateCardsIn(Array.from(grid.children).slice(previousCardCount));
                })
                .catch((error) => {
                    console.error("[archivio-servizi]", error);
                })
                .finally(() => {
                    if (thisRequestId === requestId) {
                        loadMoreButton.classList.remove("is-loading");
                    }
                });
        });
    }

    if (showLessButton) {
        showLessButton.addEventListener("click", () => {
            const cards = Array.from(grid.querySelectorAll(".archivio-servizi__card")).slice(baseCount);

            showLessButton.classList.add("is-loading");
            animateCardsOut(cards, () => {
                showLessButton.classList.remove("is-loading");
                shown = baseCount;
                updateLoadMoreUi(total > baseCount);
            });
        });
    }

    updateLoadMoreUi(total > shown);
}

export function initArchivioServizi() {
    document.querySelectorAll("[data-archivio-servizi]").forEach(initArchivioServiziSection);
}
